import * as styles from "./NavigationMenu.module.scss";
import { NavigationMenuConfig } from "./config";
import { cn } from "@/utils/cnHelper";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { IconDefinition } from "@icons/duotone";
import * as NavigationMenuPrimitive from "@radix-ui/react-navigation-menu";
import { Icon } from "@ui";
import * as React from "react";

const ChevronDownIcon = () => (
  <svg className={styles.triggerIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" height={10} width={10}>
    <path d="M233.4 105.4c12.5-12.5 32.8-12.5 45.3 0l192 192c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L256 173.3 86.6 342.6c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3l192-192z" />
  </svg>
);

type NavigationMenuItem = {
  title: string;
  icon?: IconDefinition;
  description: string;
  href: string;
};

const ProductListColumn = ({ columnConfig }: { columnConfig: { heading: string; gradient: string; items: NavigationMenuItem[] } }) => {
  return (
    <div className={styles.productColumn}>
      <div className={styles.listSubheading}>
        <div>{columnConfig.heading}</div>
        <div
          className={styles.listSubheadingDecoration}
          style={
            {
              "--base-color": columnConfig.gradient,
            } as React.CSSProperties
          }
        />
      </div>
      <ul className={styles.productList}>
        {columnConfig.items.map((item: NavigationMenuItem) => (
          <ListItem key={item.title} href={item.href} title={item.title} icon={item.icon}>
            {item.description}
          </ListItem>
        ))}
      </ul>
    </div>
  );
};

const ProductsList = () => {
  return (
    <NavigationMenuPrimitive.Item value="products">
      <NavigationMenuPrimitive.Trigger className={styles.trigger}>
        {NavigationMenuConfig.products.title}
        <ChevronDownIcon />
      </NavigationMenuPrimitive.Trigger>
      <NavigationMenuPrimitive.Content className={styles.content}>
        <div className={cn(styles.list, styles.threeColumns)}>
          <ProductListColumn columnConfig={NavigationMenuConfig.products.items.column1} />
          <ProductListColumn columnConfig={NavigationMenuConfig.products.items.column2} />
          <ProductListColumn columnConfig={NavigationMenuConfig.products.items.column3} />
        </div>
      </NavigationMenuPrimitive.Content>
    </NavigationMenuPrimitive.Item>
  );
};

const SolutionsList = () => {
  return (
    <NavigationMenuPrimitive.Item value="solutions">
      <NavigationMenuPrimitive.Trigger className={styles.trigger}>
        {NavigationMenuConfig.solutions.title}
        <ChevronDownIcon />
      </NavigationMenuPrimitive.Trigger>
      <NavigationMenuPrimitive.Content className={styles.content}>
        <ul className={cn(styles.list, styles.twoColumns)}>
          {NavigationMenuConfig.solutions.items.map((item: NavigationMenuItem) => (
            <ListItem key={item.title} href={item.href} title={item.title} icon={item.icon}>
              {item.description}
            </ListItem>
          ))}
        </ul>
      </NavigationMenuPrimitive.Content>
    </NavigationMenuPrimitive.Item>
  );
};

const InspirationList = () => {
  return (
    <NavigationMenuPrimitive.Item value="inspiration">
      <NavigationMenuPrimitive.Trigger className={styles.trigger}>
        {NavigationMenuConfig.inspiration.title}
        <ChevronDownIcon />
      </NavigationMenuPrimitive.Trigger>
      <NavigationMenuPrimitive.Content className={styles.content}>
        <ul className={cn(styles.list, styles.rows)}>
          {NavigationMenuConfig.inspiration.items.map((item: NavigationMenuItem) => (
            <ListItem key={item.title} href={item.href} title={item.title} icon={item.icon}>
              {item.description}
            </ListItem>
          ))}
        </ul>
      </NavigationMenuPrimitive.Content>
    </NavigationMenuPrimitive.Item>
  );
};

const ResourcesList = () => {
  return (
    <NavigationMenuPrimitive.Item value="resources">
      <NavigationMenuPrimitive.Trigger className={styles.trigger}>
        {NavigationMenuConfig.resources.title}
        <ChevronDownIcon />
      </NavigationMenuPrimitive.Trigger>
      <NavigationMenuPrimitive.Content className={styles.content}>
        <ul className={cn(styles.list, styles.rows)}>
          {NavigationMenuConfig.resources.items.map((item: NavigationMenuItem) => (
            <ListItem key={item.title} href={item.href} title={item.title} icon={item.icon}>
              {item.description}
            </ListItem>
          ))}
        </ul>
      </NavigationMenuPrimitive.Content>
    </NavigationMenuPrimitive.Item>
  );
};

export default function NavigationMenu() {
  return (
    <NavigationMenuPrimitive.Root className={styles.root}>
      <NavigationMenuPrimitive.List className={styles.menuList}>
        <ProductsList />
        <SolutionsList />
        <InspirationList />
        <ResourcesList />

        <NavigationMenuPrimitive.Item>
          <NavigationMenuPrimitive.Link className={styles.link} href={NavigationMenuConfig.enterprise.href}>
            {NavigationMenuConfig.enterprise.title}
          </NavigationMenuPrimitive.Link>
        </NavigationMenuPrimitive.Item>

        <NavigationMenuPrimitive.Item>
          <NavigationMenuPrimitive.Link className={styles.link} href={NavigationMenuConfig.pricing.href}>
            {NavigationMenuConfig.pricing.title}
          </NavigationMenuPrimitive.Link>
        </NavigationMenuPrimitive.Item>
      </NavigationMenuPrimitive.List>

      <div className={styles.viewportPosition}>
        <NavigationMenuPrimitive.Viewport className={styles.viewport} />
      </div>
    </NavigationMenuPrimitive.Root>
  );
}

type ListItemProps = {
  className?: string;
  children: React.ReactNode;
  title: string;
  icon?: IconDefinition;
  href: string;
};

function ListItem({ children, title, icon, ...props }: ListItemProps) {
  return (
    <li className={styles.listItemWrapper}>
      <NavigationMenuPrimitive.Link asChild>
        <a className={styles.listItemLink} {...props}>
          <div className={styles.listItemHeading}>
            <Icon icon={icon as IconProp} />
            {title}
          </div>
          <p className={styles.listItemText}>{children}</p>
        </a>
      </NavigationMenuPrimitive.Link>
    </li>
  );
}
